import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import config from "../../../config";

const StockHealthCard = () => {
    const token = localStorage.getItem("token");
    const branchId = localStorage.getItem("branch_id");

    const [summary, setSummary] = useState({
        healthy: 0,
        low: 0,
        out: 0,
        healthyPercent: 0,
        avgDays: 0,
    });
    const [lowStockItems, setLowStockItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchStockHealth = async () => {
            try {
                const res = await fetch(
                    `${config.apiUrl}/api/swalook/analytics/inventory/stock-health/?branch_name=${branchId}&days=30`,
                    {
                        headers: {
                            Authorization: `Token ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!res.ok) throw new Error("API failed");

                const json = await res.json();
                console.log("Stock health API response:", json);

                const data = json.data || {};

                setSummary({
                    healthy: data.summary?.healthy_count ?? 0,
                    low: data.summary?.low_count ?? 0,
                    out: data.summary?.out_count ?? 0,
                    healthyPercent: data.summary?.healthy_percent ?? 0,
                    avgDays: data.summary?.avgDays ?? 0,
                });

                setLowStockItems(
                    Array.isArray(data.lowStockItems) ? data.lowStockItems : []
                );
            } catch (err) {
                console.error("Stock health error:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchStockHealth();
    }, [token, branchId]);

    const donutSeries = [summary.healthy, summary.low, summary.out];

    const donutOptions = useMemo(
        () => ({
            chart: { type: "donut" },
            labels: ["Healthy", "Low Stock", "Out of Stock"],
            colors: ["#22C55E", "#F59E0B", "#EF4444"],
            legend: {
                position: "bottom",
                fontSize: "13px",
                markers: { radius: 12 },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "68%",
                        labels: {
                            show: true,
                            value: {
                                show: true,
                                fontSize: "22px",
                                fontWeight: 700,
                                formatter: () => `${summary.healthyPercent}%`,
                            },
                            total: {
                                show: true,
                                label: "SKUs",
                                formatter: () =>
                                    summary.healthy + summary.low + summary.out,
                            },
                        },
                    },
                },
            },
            dataLabels: { enabled: false },
            tooltip: {
                y: {
                    formatter: (val) => `${val} SKUs`,
                },
            },
        }),
        [summary]
    );

    if (loading) {
        return (
            <div className="bg-white rounded-[2.5rem] shadow p-6 animate-pulse h-[320px]" />
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-[2.5rem] shadow p-6 text-red-600">
                Failed to load stock health data
            </div>
        );
    }

    return (
        <div className="bg-white rounded-[2.5rem] shadow p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800">
                    Stock Health Analytics
                </h2>
                <p className="text-sm text-gray-500">
                    Overview of healthy / low / out-of-stock SKUs
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Donut */}
                <div className="flex justify-center">
                    <Chart
                        options={donutOptions}
                        series={donutSeries}
                        type="donut"
                        width={260}
                    />
                </div>

                {/* Low stock list */}
                <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        Top Low-Stock SKUs
                    </h3>

                    {lowStockItems.length > 0 ? (
                        lowStockItems.map((item, idx) => (
                            <div
                                key={idx}
                                className="flex justify-between bg-gray-50 rounded-xl px-4 py-3 mb-2"
                            >
                                <span className="text-sm font-medium text-gray-700">
                                    {item.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                    Qty: {item.qty}
                                </span>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-400">
                            No low-stock items 🎉
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StockHealthCard;
